import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Button,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { getPortalPaths, type PortalMode } from '@utils/portalPaths';

type PeriodType = 'quarterly' | 'half-yearly' | 'yearly';

function periodKeyToLabel(periodKey: string): string {
  if (!periodKey) return 'N/A';
  if (periodKey.startsWith('Q')) return periodKey.replace('-', ' ');
  if (periodKey.startsWith('H')) return periodKey.replace('-', ' ');
  if (periodKey.startsWith('Y-')) return `Yearly ${periodKey.split('-')[1]}`;
  return periodKey;
}

function periodKeyToYear(periodKey: string): number | null {
  const parts = String(periodKey).split('-');
  const year = parts.length > 1 ? Number(parts[1]) : NaN;
  return Number.isFinite(year) ? year : null;
}

function inferPeriodType(periodKey: string): PeriodType {
  if (periodKey.startsWith('Q')) return 'quarterly';
  if (periodKey.startsWith('H')) return 'half-yearly';
  if (periodKey.startsWith('Y-')) return 'yearly';
  return 'quarterly';
}

function getPeriodKeys(year: number, periodType: PeriodType): string[] {
  if (periodType === 'yearly') return [`Y-${year}`];
  if (periodType === 'half-yearly') return [`H1-${year}`, `H2-${year}`];
  return [`Q1-${year}`, `Q2-${year}`, `Q3-${year}`, `Q4-${year}`];
}

function periodKeyToEndDate(periodKey: string): string {
  const year = periodKeyToYear(periodKey) || new Date().getFullYear();
  if (periodKey.startsWith('Q1-')) return `${year}-03-31`;
  if (periodKey.startsWith('Q2-')) return `${year}-06-30`;
  if (periodKey.startsWith('Q3-')) return `${year}-09-30`;
  if (periodKey.startsWith('Q4-')) return `${year}-12-31`;
  if (periodKey.startsWith('H1-')) return `${year}-06-30`;
  if (periodKey.startsWith('H2-')) return `${year}-12-31`;
  if (periodKey.startsWith('Y-')) return `${year}-12-31`;
  return `${year}-12-31`;
}

interface PlayerRankingReportViewPageProps {
  portalMode?: PortalMode;
}

const PlayerRankingReportViewPage: React.FC<PlayerRankingReportViewPageProps> = ({ portalMode = 'player' }) => {
  const navigate = useNavigate();
  const paths = getPortalPaths(portalMode);
  const { reportId } = useParams<{ reportId: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<any>(null);

  // Exploration controls (Option 2)
  const initialYear = report?.periodKey ? periodKeyToYear(report.periodKey) : new Date().getFullYear();
  const [periodType, setPeriodType] = useState<PeriodType>('quarterly');
  const [selectedYear, setSelectedYear] = useState<number>(initialYear || new Date().getFullYear());
  const [selectedPeriodKey, setSelectedPeriodKey] = useState<string>('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const periodKeys = useMemo(() => getPeriodKeys(selectedYear, periodType), [selectedYear, periodType]);

  const loadReport = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/ranking-reports/${id}`);
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to load report');
      setReport(data.data);
    } catch (e: any) {
      console.error('Error loading ranking report:', e);
      setError(e?.message || 'Error loading report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (reportId) loadReport(reportId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  // Initialize exploration controls from loaded report (Option 1 -> Option 2)
  useEffect(() => {
    if (!report?.periodKey) return;
    const y = periodKeyToYear(report.periodKey);
    if (y) setSelectedYear(y);
    setPeriodType(inferPeriodType(report.periodKey));
    setSelectedPeriodKey(report.periodKey);
  }, [report?.periodKey]);

  const handleLookup = async (nextKey: string) => {
    if (!report) return;
    setLookupLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({
        playerId: String(report.playerId),
        categoryId: String(report.categoryId),
        sport: String(report.sport),
        periodKey: nextKey,
        status: 'submitted',
      });
      const res = await fetch(`/api/ranking-reports/lookup?${qs.toString()}`);
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Lookup failed');

      if (data.data?.exists && data.data?.reportId) {
        // Navigate to that reportId (direct fetch)
        navigate(paths.rankingReport(data.data.reportId));
      } else {
        // No submitted report for that period: show "unrated" state (empty ratings)
        setReport({
          ...report,
          reportId: null,
          periodKey: nextKey,
          periodEndDate: periodKeyToEndDate(nextKey),
          status: 'draft',
          submittedAt: null,
          strokes: (report.strokes || []).map((s: any) => ({ ...s, rating: 0 })),
        });
      }
    } catch (e: any) {
      console.error('Lookup error:', e);
      setError(e?.message || 'Lookup error');
    } finally {
      setLookupLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!report) return null;

  const isSubmitted = report.status === 'submitted';

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} variant="outlined" onClick={() => navigate(paths.reports)}>
          Back
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Ranking Report
        </Typography>
        <Chip label={isSubmitted ? 'Submitted' : 'Not submitted'} color={isSubmitted ? 'success' : 'default'} size="small" />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Chip label={String(report.sport || 'N/A')} size="small" />
        <Chip label={String(report.categoryId)} size="small" />
        <Chip label={periodKeyToLabel(String(report.periodKey))} size="small" />
        <Chip label={`Ends: ${String(report.periodEndDate || 'N/A')}`} size="small" />
      </Box>

      {/* Option 2: explore other periods */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
          Explore periods
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Period Type</InputLabel>
            <Select
              value={periodType}
              label="Period Type"
              onChange={(e) => {
                const t = e.target.value as PeriodType;
                setPeriodType(t);
                const keys = getPeriodKeys(selectedYear, t);
                setSelectedPeriodKey(keys[0] || '');
              }}
            >
              <MenuItem value="quarterly">Quarterly</MenuItem>
              <MenuItem value="half-yearly">Half-yearly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Year</InputLabel>
            <Select
              value={selectedYear}
              label="Year"
              onChange={(e) => {
                const y = Number(e.target.value);
                setSelectedYear(y);
                const keys = getPeriodKeys(y, periodType);
                setSelectedPeriodKey(keys[0] || '');
              }}
            >
              {Array.from({ length: 5 }, (_, i) => 2025 + i).map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={selectedPeriodKey}
              label="Period"
              onChange={(e) => {
                const k = String(e.target.value);
                setSelectedPeriodKey(k);
              }}
            >
              {periodKeys.map((k) => (
                <MenuItem key={k} value={k}>
                  {periodKeyToLabel(k)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            disabled={!selectedPeriodKey || lookupLoading}
            onClick={() => handleLookup(selectedPeriodKey)}
          >
            {lookupLoading ? 'Checking...' : 'Go'}
          </Button>
        </Box>
      </Paper>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Court</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Stroke</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Rating</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(report.strokes || []).map((s: any) => (
              <TableRow key={s.code}>
                <TableCell>{s.code}</TableCell>
                <TableCell>{s.courtPosition || '-'}</TableCell>
                <TableCell>{s.strokeType || '-'}</TableCell>
                <TableCell>{s.rating ?? 0}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Container>
  );
};

export default PlayerRankingReportViewPage;








